import { useNavigate } from "react-router-dom";
import { useService } from "@/contexts/ServiceContext";
import { Shield, Stethoscope, Mountain, Plane } from "lucide-react";

const SERVICE_CARDS = [
  {
    id: "ground",
    label: "PCP / ACP-G",
    subtitle: "Ground EMS Protocols",
    description: "Primary & Advanced Care Paramedic guidelines for ground ambulance operations",
    icon: Stethoscope,
    color: "from-blue-600 to-blue-800",
    border: "border-blue-500/40 hover:border-blue-400",
    glow: "hover:shadow-blue-500/20",
    protocols: 94,
  },
  {
    id: "sfa_emr",
    label: "SFA / EMR",
    subtitle: "Standard First Aid & EMR",
    description: "Standard First Aid and Emergency Medical Responder guidelines",
    icon: Shield,
    color: "from-emerald-600 to-emerald-800",
    border: "border-emerald-500/40 hover:border-emerald-400",
    glow: "hover:shadow-emerald-500/20",
    protocols: 36,
  },
  {
    id: "wes",
    label: "PCP-PS",
    subtitle: "Wilderness / Paramedic Specialist",
    description: "PCP-PS guidelines for wilderness and extended scope operations",
    icon: Mountain,
    color: "from-orange-600 to-orange-800",
    border: "border-orange-500/40 hover:border-orange-400",
    glow: "hover:shadow-orange-500/20",
    protocols: 41,
  },
  {
    id: "air_adult",
    label: "Air Ambulance — Adult",
    subtitle: "Air Medical Transport",
    description: "PCP / ACP-F / CCP protocols for fixed-wing and rotor-wing adult patients",
    icon: Plane,
    color: "from-violet-600 to-violet-800",
    border: "border-violet-500/40 hover:border-violet-400",
    glow: "hover:shadow-violet-500/20",
    protocols: 64,
  },
  {
    id: "air_pediatric",
    label: "Air Ambulance — OB/NEO/PEDS",
    subtitle: "Air Medical Transport",
    description: "CCP protocols for obstetrics, neonatal, and pediatric air transport",
    icon: Plane,
    color: "from-pink-600 to-pink-800",
    border: "border-pink-500/40 hover:border-pink-400",
    glow: "hover:shadow-pink-500/20",
    protocols: 47,
  },
];

export default function ServiceSelector() {
  const { switchService } = useService();
  const navigate = useNavigate();

  const handleSelect = (serviceId) => {
    switchService(serviceId);
    try { localStorage.setItem("yems-service-chosen", "true"); } catch {}
    navigate("/");
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex flex-col items-center justify-center p-6" data-testid="service-selector-page">
      <div className="max-w-4xl w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="service-selector-title">
            Select Your Guideline Set
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-lg mx-auto">
            Choose the clinical guidelines for your scope of practice. You can switch anytime from the sidebar.
          </p>
        </div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="service-cards-grid">
          {SERVICE_CARDS.map((svc) => {
            const Icon = svc.icon;
            return (
              <button
                key={svc.id}
                onClick={() => handleSelect(svc.id)}
                className={`group relative text-left rounded-xl border-2 ${svc.border} bg-card p-5 transition-all duration-200 hover:scale-[1.02] hover:shadow-lg ${svc.glow} focus:outline-none focus:ring-2 focus:ring-primary/50`}
                data-testid={`service-card-${svc.id}`}
              >
                {/* Icon + Badge */}
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${svc.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground bg-secondary/60 px-2 py-0.5 rounded">
                    {svc.protocols} protocols
                  </span>
                </div>

                {/* Title */}
                <h2 className="font-heading text-lg font-bold group-hover:text-primary transition-colors">
                  {svc.label}
                </h2>
                <p className="text-xs text-muted-foreground font-medium mt-0.5">{svc.subtitle}</p>

                {/* Description */}
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                  {svc.description}
                </p>
              </button>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          Yukon EMS Clinical Guidelines &mdash; V1.2
        </p>
      </div>
    </div>
  );
}
